import { StatusPill } from '@pages/Library/components/MediaCard/components/StatusPill';
import {
  getDisplayProgress,
  getMediaAltText,
  getMediaDisplayStatus,
  getStatusLabel,
  getStatusTooltip,
  isProcessingWithVideo,
} from '@pages/Library/utils/mediaStatus';
import { applyProgressBarStyles } from '@pages/Library/utils/progressBar';
import { getUploadSubtext } from '@pages/Library/utils/uploadCalculations';
import { useUploadManager } from '@stores/UploadManager/UploadManager';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import { useEffect, useRef } from 'react';
import css from '../LibraryContentMediaTable.module.scss';
import { CellProps } from './types';

export const StatusCell = observer(({ media }: CellProps) => {
  const uploadManager = useUploadManager();
  const uploadBarRef = useRef<HTMLDivElement>(null);
  const processingBarRef = useRef<HTMLDivElement>(null);

  // Prefer live status from store so progress updates re-render (list view rows are memoized and
  // keep stale media.uploadStatus; grid view rebuilds every render so doesn't need this).
  const currentUploadStatus = media.isUploading
    ? (uploadManager.uploadStatus[media._id] ??
      uploadManager.uploadStatus[uploadManager.mediaIdToLocalUUID[media._id]] ??
      media.uploadStatus ??
      null)
    : null;
  const isUploading = currentUploadStatus != null;
  const uploadProgress = isUploading
    ? Math.max(0, Math.min(100, Math.floor(currentUploadStatus?.progress || 0)))
    : null;

  // Calculate processing progress
  const isProcessing = media.status === 'processing';
  const isRetrieving = media.status === 'queued' || media.status === 'uploading';

  // Use shared utility for progress calculation
  const normalizedProgress = isProcessing || isRetrieving ? getDisplayProgress(media) : null;

  // Update upload bar
  useEffect(() => {
    if (uploadProgress === null) return;
    applyProgressBarStyles(uploadBarRef.current, uploadProgress);
  }, [uploadProgress]);

  // Update processing bar
  useEffect(() => {
    if (normalizedProgress == null) return;
    applyProgressBarStyles(processingBarRef.current, normalizedProgress);
  }, [normalizedProgress]);

  // Handle uploading files specially
  // But skip if backend has already transitioned to processing states (upload is done)
  const backendHasFile =
    !media.isUploading &&
    (media.status === 'queued' || media.status === 'processing' || media.status === 'completed');
  if (isUploading && !backendHasFile) {
    const subtext = getUploadSubtext(currentUploadStatus, uploadProgress || 0);

    const displayText =
      currentUploadStatus?.status === 'queued'
        ? getStatusLabel('queued-upload')
        : `Uploading ${uploadProgress}%`;
    const tooltipText =
      currentUploadStatus?.status === 'queued'
        ? getStatusTooltip('queued-upload', getMediaAltText(media))
        : `Uploading ${getMediaAltText(media)}${subtext ? `\n${subtext}` : ''}`;

    return (
      <div className={css.fullWidthCell} style={{ height: '24px' }}>
        <StatusPill mini tooltip={tooltipText} status="uploading">
          <div className={classnames(css.centerPillContents, { [css.hasSubtext]: !!subtext })}>
            <div ref={uploadBarRef} className={css.bar} />
            <div className={css.text}>
              <div className={css.mainText}>{displayText}</div>
              {subtext && <span className={css.subtext}>{subtext}</span>}
            </div>
          </div>
        </StatusPill>
      </div>
    );
  }

  if (isProcessing || isRetrieving) {
    // Determine the correct display status based on actual media state
    let displayStatus: 'queued' | 'processing-with-video' | 'processing-without-video';
    if (isProcessing) {
      displayStatus = isProcessingWithVideo(media)
        ? 'processing-with-video'
        : 'processing-without-video';
    } else {
      // isRetrieving (queued or uploading backend status) - show as queued
      displayStatus = 'queued';
    }
    const statusText = getStatusLabel(displayStatus, normalizedProgress ?? undefined);
    const tooltip = getStatusTooltip(displayStatus, getMediaAltText(media));

    return (
      <div className={css.fullWidthCell}>
        <StatusPill mini tooltip={tooltip} status={media.status}>
          <div className={css.centerPillContents}>
            <div ref={processingBarRef} className={css.bar} />
            <div className={css.text}>
              <span>{statusText}</span>
            </div>
          </div>
        </StatusPill>
      </div>
    );
  }

  return (
    <span className={classnames(css.statusCell, css[`status-${media.status}`])}>
      {getStatusLabel(getMediaDisplayStatus(media))}
    </span>
  );
});
